import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  AdminPageHeader,
  Button,
  ButtonSize,
  ButtonStyles,
  ButtonTypes,
  Modal,
  TableWithSearchTemplate,
  Theme,
} from '../../../ui/components';
import { LoggerService } from '../../../ui/utils';
import { useAdminToast } from '../../../ui/components/AdminToast/AdminToast';
import { useAPICall } from '../../../hooks';
import RewardService from '../../../sdk/services/rewardService';
import getRewardListConfig, { dataSearchOption } from './rewardListConfig'; // Import the reward configuration

export const AdminRewardList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [selectedRowId, setSelectedRowId] = useState('');
  const [showDelete, setShowDelete] = useState(false);
  const [data, setData] = useState([]);

  const apiCall = useAPICall();

  const { adminToast } = useAdminToast();

  useEffect(() => {
    fetchRewardList();
  }, []);

  const fetchRewardList = async () => {
    try {
      const response = await apiCall(RewardService.getRewardList);
      setData(response?.data?.data ?? []);
    } catch (error) {
      LoggerService.log('Failed to retrieve reward list', error);
    }
  };

  const onEdit = (id: string) => {
    navigate(`/admin/reward-setup/edit?=${id}`);
  };

  const onClick = (id: string) => {
    //to show pop up modal
    setSelectedRowId(id);
    setShowDelete(true);
  };

  const buttonConfig = [
    {
      title: t('admin.rewardMaster.addReward'),
      buttonStyle: ButtonStyles.SOLID,
      onClick: () => {
        navigate(`/admin/reward-setup/edit?=new`);
      },
    },
  ];

  const onDeleteAction = async () => {
    try {
      const response = await apiCall(RewardService.deleteReward, `${selectedRowId}`);

      if (response?.status === 200) {
        // Assuming the server responds with success status
        LoggerService.log('Reward deleted successfully');
        adminToast({
          message: 'Reward deleted successfully',
          theme: Theme.SUCCESS,
        });
        fetchRewardList();
      } else {
        LoggerService.log('Failed to delete reward');
      }
    } catch (error) {
      LoggerService.log('Error deleting reward', error);
    } finally {
      // Close the modal whether the deletion was successful or not
      setShowDelete(false);
    }
  };

  const deletePrompt = (
    <>
      {showDelete && (
        <Modal
          primaryButtonLabel={''}
          headline={`${t('admin.rewardMaster.deleteReward')}`}
          closeIcon={false}
          containerWidth={'460px'}
          primaryButton={
            <Button
              title={t('admin.rewardMaster.confirm')}
              type={ButtonTypes.BUTTON}
              onClick={() => {
                onDeleteAction();
              }}
              size={ButtonSize.SMALL}
              buttonStyle={ButtonStyles.SOLID}
              data-testid="test-add-button-popup"
            />
          }
          secondaryButton={
            <Button
              title={t('admin.rewardMaster.cancel')}
              type={ButtonTypes.BUTTON}
              onClick={() => {
                setShowDelete(false);
              }}
              size={ButtonSize.SMALL}
              buttonStyle={ButtonStyles.OUTLINE}
              data-testid="test-cancel-button-popup-delete-modal"
            />
          }
        />
      )}
    </>
  );

  return (
    <div>
      <AdminPageHeader title={t('admin.rewardMaster.title')} button={buttonConfig} />
      <TableWithSearchTemplate
        dataSource={data} // Replace with the actual data
        onReset={() => void 0}
        searchByStrip
        getColumns={getRewardListConfig} // Use the reward configuration
        data={dataSearchOption}
        rowKey="recId"
        selected={selectedRowId}
        cb={{ onEdit, onClick }}
        onRowClick={row => {
          setSelectedRowId(row.recId);
        }}
      />
      {deletePrompt}
    </div>
  );
};
